import { useNode, useEditor } from "@craftjs/core";
import React from "react";
import { Box, Typography } from "@mui/material";
import { useInlineEdit } from "../../hooks/useInlineEdit";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, SliderField, SettingsSection } from "../ui/form/SettingsField";

export interface NavLogoProps {
  logoText?: string;
  logoUrl?: string;
  linkUrl?: string;
  fontSize?: number;
  height?: number;
}

export const NavLogo = ({ logoText = "Clinic", logoUrl, linkUrl = "/", fontSize = 20, height = 40 }: NavLogoProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { getEditableProps } = useInlineEdit(setProp);

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (editorEnabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        flexShrink: 0,
        ...(selected && { ...selectedOutlineSx, outlineOffset: "2px" }),
      }}
    >
      <Box
        component="a"
        href={linkUrl}
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={logoText}
            sx={{
              height: `${height}px`,
              width: "auto",
            }}
          />
        ) : (
          <Typography
            {...getEditableProps("logoText", logoText || "", (v) => { setProp((p: NavLogoProps) => { p.logoText = v; }); })}
            sx={{
              fontSize: `${fontSize}px`,
              fontWeight: 700,
              color: "inherit",
            }}
          >
            {logoText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const NavLogoSettings = () => {
  const {
    actions: { setProp },
    logoText,
    logoUrl,
    linkUrl,
    fontSize,
    height,
  } = useNode((node) => ({
    logoText: node.data.props.logoText,
    logoUrl: node.data.props.logoUrl,
    linkUrl: node.data.props.linkUrl,
    fontSize: node.data.props.fontSize,
    height: node.data.props.height,
  }));

  return (
    <SettingsSection>
      <TextField
        label="Logo Text"
        value={logoText}
        onChange={(v) => setProp((props: NavLogoProps) => (props.logoText = v))}
      />

      <div>
        <TextField
          label="Logo Image URL (optional)"
          value={logoUrl || ""}
          onChange={(v) => setProp((props: NavLogoProps) => (props.logoUrl = v || undefined))}
          placeholder="https://example.com/logo.png"
        />
        <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px" }}>Leave empty to use text logo</p>
      </div>

      <TextField
        label="Link URL"
        value={linkUrl}
        onChange={(v) => setProp((props: NavLogoProps) => (props.linkUrl = v))}
      />

      {!logoUrl && (
        <SliderField
          label="Font Size"
          value={fontSize}
          onChange={(v) => setProp((props: NavLogoProps) => (props.fontSize = v))}
          min={14}
          max={32}
          unit="px"
        />
      )}

      {logoUrl && (
        <SliderField
          label="Logo Height"
          value={height}
          onChange={(v) => setProp((props: NavLogoProps) => (props.height = v))}
          min={24}
          max={80}
          unit="px"
        />
      )}
    </SettingsSection>
  );
};

NavLogo.craft = {
  displayName: "Nav Logo",
  props: {
    logoText: "Clinic",
    logoUrl: undefined,
    linkUrl: "/",
    fontSize: 20,
    height: 40,
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: NavLogoSettings,
  },
};
